/**
 * Manuscript builder — derive a `Manuscript` from a Vite `import.meta.glob`
 * raw-import record so demos can wire chapter content with a few lines.
 *
 * Most CSK companion manuscripts live as flat Markdown files under
 * `theory/manuscripts/.../<Title>/{monograph,handbook,chapters}/*.md`, with a
 * leading numeric or alphabetic id followed by an underscore- or
 * dash-separated title slug. This helper extracts the id + title pairs from
 * a glob record, sorts them by id, and pairs them with a `loadChapter`
 * function the viewer can call back to fetch the chapter body — all without
 * a sidecar JSON catalog. Demos that need richer metadata (parts, reading
 * paths, custom word counts) build the `Manuscript` by hand and use the
 * `composeChapterLoaders` helper to combine multiple sources.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type {
  LoadChapterFn,
  Manuscript,
  ManuscriptAppendix,
  ManuscriptChapter,
  ManuscriptItemKind,
} from "./manuscript-viewer/types.js";

export interface ManuscriptBuilderOptions {
  /** Stable identifier for the manuscript (unique across the viewer). */
  readonly id: string;
  /** Display label shown in the manuscript tab strip and header. */
  readonly label: string;
  /** Optional eyebrow text rendered above the title. */
  readonly eyebrow?: string;
  /** Optional tagline shown under the title. Defaults to a derived count. */
  readonly tagline?: string;
  /**
   * Predicate used to classify entries as appendices. Defaults to "id starts
   * with an uppercase ASCII letter" — matches the `A1_…`, `B-…` convention
   * used across the CSK manuscripts.
   */
  readonly isAppendix?: (id: string) => boolean;
  /**
   * Override the id derivation from a filename leaf. By default the leading
   * `[0-9A-Za-z]+` run is the id (`01-Introduction` → `01`,
   * `A1_Glossary` → `A1`). Override for manuscripts where the default
   * collides — e.g. `Chapter-1-Foo` and `Chapter-2-Bar` would both parse to
   * id `Chapter` under the default, so the override should return a unique
   * identifier per file (`Chapter-1`, `Chapter-2`, …).
   */
  readonly deriveId?: (input: { leaf: string }) => string;
  /**
   * Override the title derivation from a filename leaf. By default the
   * leading id segment is stripped (`01-Introduction` → `Introduction`) and
   * underscores and dashes are normalized to spaces.
   */
  readonly deriveTitle?: (idAndRemainder: {
    id: string;
    remainder: string;
    leaf: string;
  }) => string;
}

export interface BuiltManuscript {
  readonly manuscript: Manuscript;
  readonly loadChapter: LoadChapterFn;
}

export interface ManuscriptLeafInput {
  readonly leaf: string;
}

export interface ManuscriptFileFilterOptions {
  readonly includeSuffixes?: readonly string[];
  readonly skipLeaves?: ReadonlySet<string> | readonly string[];
}

const DEFAULT_IS_APPENDIX = (id: string): boolean => /^[A-Z]/.test(id);

const DEFAULT_DERIVE_TITLE = (input: { id: string; remainder: string; leaf: string }): string => {
  const candidate = (input.remainder !== "" ? input.remainder : input.leaf).trim();
  return candidate.replaceAll("_", " ").replaceAll("-", " ").trim();
};

const WORD_TOKEN_PATTERN = /\S+/g;
const ROMAN_PARTS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"] as const;

function fileLeafName(path: string): string {
  const slash = path.lastIndexOf("/");
  const leaf = slash >= 0 ? path.slice(slash + 1) : path;
  return leaf.replace(/\.md$/i, "").replace(/\.markdown$/i, "");
}

export function deriveLeafId({ leaf }: ManuscriptLeafInput): string {
  return leaf;
}

export function deriveChapterId({ leaf }: ManuscriptLeafInput): string {
  const match = /^Chapter-(\d+[a-z]?)-/i.exec(leaf);
  return match?.[1] ?? leaf;
}

export function deriveChapterTitle({ leaf }: ManuscriptLeafInput): string {
  return leaf
    .replace(/^Chapter-\d+[a-z]?-/i, "")
    .replaceAll("-", " ")
    .trim();
}

export function deriveChapterOrAppendixId({ leaf }: ManuscriptLeafInput): string {
  const chapterMatch = /^Chapter-(\d+[a-z]?)-/i.exec(leaf);
  if (chapterMatch?.[1] !== undefined) return chapterMatch[1];
  const appendixMatch = /^Appendix-([A-Za-z]\d*)-/i.exec(leaf);
  return appendixMatch?.[1]?.toUpperCase() ?? leaf;
}

export function derivePaddedChapterOrAppendixId({ leaf }: ManuscriptLeafInput): string {
  const chapterMatch = /^Chapter-(\d+)-/i.exec(leaf);
  if (chapterMatch?.[1] !== undefined) return chapterMatch[1].padStart(2, "0");
  const appendixMatch = /^Appendix-([A-Za-z]\d*)-/i.exec(leaf);
  return appendixMatch?.[1]?.toUpperCase() ?? leaf;
}

export function deriveChapterOrAppendixTitle({ leaf }: ManuscriptLeafInput): string {
  return leaf
    .replace(/^(?:Chapter|Appendix)-[0-9A-Za-z]+-/i, "")
    .replaceAll("-", " ")
    .trim();
}

export function deriveNumberOrAppendixId({ leaf }: ManuscriptLeafInput): string {
  const chapterMatch = /^(\d{2})-/u.exec(leaf);
  if (chapterMatch?.[1] !== undefined) return chapterMatch[1];
  const appendixMatch = /^Appendix-([A-Za-z])-/u.exec(leaf);
  return appendixMatch?.[1]?.toUpperCase() ?? leaf;
}

export function deriveNumberOrAppendixTitle({ leaf }: ManuscriptLeafInput): string {
  return leaf
    .replace(/^\d{2}-/u, "")
    .replace(/^Appendix-[A-Za-z]-/u, "")
    .replaceAll("-", " ")
    .trim();
}

export function deriveRomanPartOrAppendixId({ leaf }: ManuscriptLeafInput): string {
  const partMatch = /^Part-([IVX]+)-/i.exec(leaf);
  const numeral = partMatch?.[1]?.toUpperCase();
  if (numeral !== undefined) {
    const index = ROMAN_PARTS.indexOf(numeral as (typeof ROMAN_PARTS)[number]);
    return index >= 0 ? `P${(index + 1).toString().padStart(2, "0")}` : `P-${numeral}`;
  }
  const appendixMatch = /^Appendix-([A-Z])-/i.exec(leaf);
  return appendixMatch?.[1]?.toUpperCase() ?? leaf;
}

export function filterManuscriptFiles(
  files: Record<string, string>,
  { includeSuffixes, skipLeaves = [] }: ManuscriptFileFilterOptions,
): Record<string, string> {
  const skip = skipLeaves instanceof Set ? skipLeaves : new Set(skipLeaves);
  return Object.fromEntries(
    Object.entries(files).filter(([path]) => {
      if (
        includeSuffixes !== undefined &&
        !includeSuffixes.some((suffix) => path.endsWith(suffix))
      ) {
        return false;
      }
      return !skip.has(fileLeafName(path));
    }),
  );
}

function parseLeaf(leaf: string): { id: string; remainder: string } {
  const match = /^([0-9A-Za-z]+)[_\- ]?(.*)$/.exec(leaf);
  if (match === null) return { id: leaf, remainder: "" };
  return { id: match[1] ?? leaf, remainder: match[2] ?? "" };
}

function wordCount(text: string): number {
  let count = 0;
  WORD_TOKEN_PATTERN.lastIndex = 0;
  while (WORD_TOKEN_PATTERN.exec(text) !== null) {
    count += 1;
  }
  return count;
}

/**
 * Build a `Manuscript` plus its `loadChapter` callback from a Vite glob.
 *
 * Usage:
 *
 *     const FILES = import.meta.glob(
 *       "../../../theory/manuscripts/<NAME>/monograph/*.md",
 *       { query: "?raw", import: "default", eager: true },
 *     ) as Record<string, string>;
 *
 *     const { manuscript, loadChapter } = buildManuscriptFromFiles(
 *       { id: "monograph", label: "Monograph" },
 *       FILES,
 *     );
 *
 *     return <ManuscriptViewer manuscripts={[manuscript]} loadChapter={loadChapter} />;
 */
export function buildManuscriptFromFiles(
  options: ManuscriptBuilderOptions,
  files: Record<string, string>,
): BuiltManuscript {
  const isAppendix = options.isAppendix ?? DEFAULT_IS_APPENDIX;
  const deriveTitle = options.deriveTitle ?? DEFAULT_DERIVE_TITLE;
  const deriveId = options.deriveId;

  const chapters: ManuscriptChapter[] = [];
  const appendices: ManuscriptAppendix[] = [];
  const contentByKey = new Map<string, string>();
  const seenIds = new Set<string>();

  const sortedPaths = Object.keys(files)
    .slice()
    .sort((a, b) => a.localeCompare(b, "en", { numeric: true }));

  for (const path of sortedPaths) {
    const content = files[path] ?? "";
    const leaf = fileLeafName(path);
    const parsed = parseLeaf(leaf);
    const id = deriveId !== undefined ? deriveId({ leaf }) : parsed.id;
    if (seenIds.has(id)) {
      throw new Error(
        `buildManuscriptFromFiles: duplicate chapter id "${id}" derived from "${leaf}". ` +
          `Pass a deriveId callback that returns a unique id per filename.`,
      );
    }
    seenIds.add(id);
    const title = deriveTitle({ id, remainder: parsed.remainder, leaf });
    const words = wordCount(content);
    if (isAppendix(id)) {
      appendices.push({ id, title, words });
      contentByKey.set(`appendix:${id}`, content);
    } else {
      chapters.push({ id, title, words });
      contentByKey.set(`chapter:${id}`, content);
    }
  }

  const tagline =
    options.tagline ??
    `${chapters.length} chapters${appendices.length > 0 ? ` · ${appendices.length} appendices` : ""}`;

  const manuscript: Manuscript = {
    id: options.id,
    label: options.label,
    ...(options.eyebrow !== undefined ? { eyebrow: options.eyebrow } : {}),
    tagline,
    chapters,
    ...(appendices.length > 0 ? { appendices } : {}),
  };

  const loadChapter: LoadChapterFn = ({ manuscriptId, itemId, kind }) => {
    if (manuscriptId !== options.id) return null;
    return contentByKey.get(loadKey(kind, itemId)) ?? null;
  };

  return { manuscript, loadChapter };
}

function loadKey(kind: ManuscriptItemKind, itemId: string): string {
  return `${kind}:${itemId}`;
}

/**
 * Combine multiple `loadChapter` functions into one. Each loader is consulted
 * in order; the first non-null result wins.
 *
 * Useful when a viewer hosts more than one manuscript and each manuscript was
 * built separately via `buildManuscriptFromFiles`.
 */
export function composeChapterLoaders(loaders: readonly LoadChapterFn[]): LoadChapterFn {
  return async (args) => {
    for (const loader of loaders) {
      const result = await Promise.resolve(loader(args));
      if (result !== null) return result;
    }
    return null;
  };
}
